import {
    AgencyMemberId, HRDocumentId, HRDocumentTypeId, HRQuestionGroupId, HRQuestionId, HRQuestionTemplateId, HRSectionId, HRSubSectionId, HRSectionStageId, AgencyId, CaregiverDocumentTypeId, OfficeId, CaregiverCertification
} from "./ObjectIds";
import { CertificationSettings } from "./Agency";
import { AddressComponents } from "./Visit";

export interface SectionStagesByCertificationResponse {
    stages: HRSectionStage[];
}

export interface HRSectionStage {
    id: HRSectionStageId;
    agencyId: AgencyId;
    label: string;
    order: number;
    labelColor: string;
    certification: CaregiverCertification;
}

// for returning to HR app
export interface HRApplicationForm {
    sections: HRSection[];
    settings: HRApplicationFormSettings;
    caregiverSectionStageId: HRSectionStageId | null;
    caregiverSectionStageLabel: string | null;
}

export interface HRApplicationFormSettings {
    certificationSettings: CertificationSettings | null;
}

export interface HRSection {
    data: HRSectionData;
    subSections: HRSubSection[];
}

export interface HRSectionData {
    id: HRSectionId;
    title: string;
    subtitle: string | null;
    isStop: boolean;
    HRSectionStageId: HRSectionStageId | null;
    HRSectionStageLabel: string | null;
}

export interface SubsectionDocumentData {
    isDocumentGenerated: boolean;
    doc: string | null;
}

export interface HRSubSection {
    data: HRSubSectionData;
    document: SubsectionDocumentData | null;
    questionGroups: HRQuestionGroup[];
}

export interface HRSubSectionSettings {
    testSettings: HRTestSettings | null;
    availabilityPlaceholder: HRAvailabilityPlaceholderSettings | null;
}

export interface HRTestSettings {
    // TODO rename?
    testFailText: string;
    testSuccessText: string;
    testRetryText: string;
    maxWrongAnswers: number;
    testMaxTries: number;
    shouldShowWrongAnswers: boolean;
}

export interface HRAvailabilityPlaceholderSettings {
    showHoursQuestion: boolean;
    showDistanceQuestion: boolean;
}


export interface HRSubSectionData {
    id: HRSubSectionId;
    title: string;
    subtitle: string | null;
    settings: HRSubSectionSettings | null;
}

export interface HRQuestionGroup {
    data: HRQuestionGroupData;
    questions: HRQuestion[];
}

export interface HRQuestionGroupData {
    id: HRQuestionGroupId;
    title: string;
    subtitle: string | null;
}

export type HRQuestion =
    HRQuestion.TextQuestion |
    HRQuestion.AddressQuestion |
    HRQuestion.EmailQuestion |
    HRQuestion.NumericQuestion |
    HRQuestion.DollarQuestion |
    HRQuestion.PhoneNumberQuestion |
    HRQuestion.SSNQuestion |
    HRQuestion.FullDateQuestion |
    HRQuestion.BirthDateQuestion |
    HRQuestion.MonthYearDateQuestion |
    HRQuestion.YearDateQuestion |
    HRQuestion.RadioQuestion |
    HRQuestion.DropDownQuestion |
    HRQuestion.CheckboxQuestion |
    HRQuestion.SignatureQuestion |
    HRQuestion.GeneralSignatureQuestion |
    HRQuestion.StartAndEndTimeQuestion |
    HRQuestion.StarQuestion |
    HRQuestion.Placeholder |HRQuestion.TitleText |
    HRQuestion.PlainText |
    HRQuestion.DocumentQuestion |
    HRQuestion.DocumentWithExpiryQuestion |
    HRQuestion.AmericanTestQuestion |
    HRQuestion.FreeTextQuestion |
    HRQuestion.DownloadableFileQuestion;

export namespace HRQuestion {

    export const questionTypes = {
        Text: "Text",
        Address: "Address",
        Email: "Email",
        Numeric: "Numeric",
        Dollar: "Dollar",
        PhoneNumber: "PhoneNumber",
        SSN: "SSN",
        FullDate: "FullDate",
        BirthDate: "BirthDate",
        MonthYearDate: "MonthYearDate",
        YearDate: "YearDate",
        Radio: "Radio",
        DropDown: "DropDown",
        Checkbox: "Checkbox",
        Signature: "Signature",
        GeneralSignature: "GeneralSignature",
        StartAndEndTime: "StartAndEndTime",
        Star: "Star",
        Placeholder: "Placeholder",
        Document: "Document",
        DocumentWithExpiry: "DocumentWithExpiry",
        AmericanTest: "AmericanTest",
        FreeText: "FreeText",
        TitleText: "TitleText",
        PlainText: "PlainText",
        DownloadableFile: "DownloadableFile"
    };
    export type QuestionType = keyof typeof questionTypes;
    export function isValidQuestionType(x: string): x is QuestionType {
        return questionTypes.hasOwnProperty(x);
    }

    interface QuestionData {
        id: HRQuestionId;
        textId: HRQuestionTemplateId;
        questionText: string;
        isMandatory: boolean;

        parentId: HRQuestionId | null;
        parentAnswer: string | null;
        isHidden: boolean;

        readonly type: QuestionType;
    }

    export interface TextQuestion extends QuestionData {
        readonly type: "Text";
        answer: string | null;
    }

    export interface AddressQuestion extends QuestionData {
        readonly type: "Address";
        answer: string | null;
    }

    export interface EmailQuestion extends QuestionData {
        readonly type: "Email";
        answer: string | null;
    }

    export interface NumericQuestion extends QuestionData {
        readonly type: "Numeric";
        answer: string | null;
    }

    export interface DollarQuestion extends QuestionData {
        readonly type: "Dollar";
        answer: string | null;
    }

    export interface PhoneNumberQuestion extends QuestionData {
        readonly type: "PhoneNumber";
        answer: string | null;
    }

    export interface SSNQuestion extends QuestionData {
        readonly type: "SSN";
        answer: string | null;
    }

    export interface FullDateQuestion extends QuestionData {
        readonly type: "FullDate";
        answer: string | null;
    }

    export interface BirthDateQuestion extends QuestionData {
        readonly type: "BirthDate";
        answer: string | null;
    }

    export interface MonthYearDateQuestion extends QuestionData {
        readonly type: "MonthYearDate";
        answer: string | null;
    }

    export interface YearDateQuestion extends QuestionData {
        readonly type: "YearDate";
        answer: string | null;
    }

    export interface RadioQuestion extends QuestionData {
        readonly type: "Radio";
        possibleAnswers: PossibleAnswer[];
        chosenAnswerId: PossibleAnswerId | null;
    }

    export interface DropDownQuestion extends QuestionData {
        readonly type: "DropDown";
        possibleAnswers: PossibleAnswer[];
        chosenAnswerId: PossibleAnswerId | null;
    }

    export interface CheckboxQuestion extends QuestionData {
        readonly type: "Checkbox";
        possibleAnswers: PossibleAnswer[];
        chosenAnswerIds: PossibleAnswerId[] | null;
    }

    // Same as checkbox for now, should prolly be called "CheckBoxWithHtmlHeader" or just add indication to check box
    export interface FreeTextQuestion extends QuestionData {
        readonly type: "FreeText";
        possibleAnswers: PossibleAnswer[];
        chosenAnswerIds: PossibleAnswerId[] | null;
    }

    export interface StarQuestion extends QuestionData {
        readonly type: "Star";
        answer: number | null;
    }

    export interface SignatureQuestion extends QuestionData {
        readonly type: "Signature";
        /**
         * base64 data-url png of signature
         */
        answer: string | null;
    }

    export interface GeneralSignatureQuestion extends QuestionData {
        readonly type: "GeneralSignature";
        /**
         * base64 data-url png of signature
         */
        answer: GeneralSignatureAnswer | null;
    }

    export interface StartAndEndTimeQuestion extends QuestionData {
        readonly type: "StartAndEndTime";
        answer: null | { startTime: number, endTime: number };
    }

    export interface Placeholder extends QuestionData {
        readonly type: "Placeholder";
        isMandatory: false;
    }

    export interface TitleText extends QuestionData {
        readonly type: "TitleText";
        isMandatory: false;
    }

    export interface PlainText extends QuestionData {
        readonly type: "PlainText";
        isMandatory: false;
    }


    export interface DocumentQuestion extends QuestionData {
        readonly type: "Document";
        /**
         * data uri
         */
        answer: string | null;
        documentTypeToUpload: CaregiverDocumentTypeId | null;
        canUploadFromGallery: boolean;
    }

    export interface DocumentWithExpiryQuestion extends QuestionData {
        readonly type: "DocumentWithExpiry";
        /**
         * data uri
         */
        answer: DocumentWithExpiryAnswer | null;
        documentTypeToUpload: CaregiverDocumentTypeId | null;
    }

    export interface AmericanTestQuestion extends QuestionData {
        readonly type: "AmericanTest";

        possibleAnswers: PossibleAnswer[];
        chosenAnswerId: PossibleAnswerId | null;
        correctAnswer: PossibleAnswerId;
    }

    export interface DownloadableFileQuestion extends QuestionData {
        readonly type: "DownloadableFile";
        downloaded: boolean;
        answer: string | null;
    }
}

export interface DocumentWithExpiryAnswer {
    document: string;
    expiryDate: string | null;
}

export interface PossibleAnswer {
    id: PossibleAnswerId;
    text: string;
}

export type PossibleAnswerId = number;

export interface HRApplicationFormForAgency {
    sections: HRSectionForAgency[];
}

export interface HRSectionForAgency {
    data: HRSectionData;
    subSections: HRSubSectionForAgency[];
}

export interface HRSubSectionForAgency {
    data: HRSubSectionData;
    questionGroups: HRQuestionGroupForAgency[];
}

export interface HRQuestionGroupForAgency {
    data: HRQuestionGroupData;
    questions: HRQuestionForAgency[];
}

export interface HRQuestionForAgency {
    id: HRQuestionId;
    questionText: string;
    possibleAnswers: PossibleAnswer[] | null;
    document: HRDocumentTypeId | null;
    isOverridable: boolean;
    isMandatory: boolean;
    isShown: boolean;
}

export interface HRAgencyQuestionSettings {
    id: HRQuestionId;
    isMandatory: boolean;
    isShown: boolean;
}

export interface HRAgencyDocumentSettings {
    id: HRDocumentTypeId;
    isMandatory: boolean;
}

export interface HRDocumentTypes {
    documentTypes: HRDocumentType[];
}
export interface HRDocumentType {
    id: HRDocumentTypeId;
    name: string;
    expires: boolean;
    exampleUrl: string | null;
    isMandatory: boolean;
    sectionLabel: string | null;
}

// For web app
export interface HRDocumentsData {
    documents: HRDocumentData[];
}
export interface HRDocumentData {
    id: HRDocumentId;
    documentType: HRDocumentTypeId;
    createdAt: string;
    createdBy: AgencyMemberId;
    expirationDate: string | null;
    isGenerated: boolean;
}
export interface HRDocumentFile {
    // signed url to S3 file
    url: string;
}

// For HR app
export interface HRAppDocuments {
    documents: HRAppDocument[];
}
export interface HRAppDocument {
    title: string;
    url: string;
    signedAt: string | null;
}

export interface HRAnswerResult {
    isSuccessful: boolean;
    reason?: string;
}

export interface HRAppTemporaryDocument {
    title: string;
    url: string;
    key: string;
    bucket: string;
    signedAt: string;
    docType: number | null;
}

export interface HRAppDocumentToStore {
    bucket: string;
    key: string;
    docType: number | null;
}

export interface HRLogoutTimer {
    milliseconds: number;
}

export interface GeneralSignatureAnswer {
    signature: string | null;
    generalSignaturePerson: string | null;
}

export interface CaregiverDocumentTypeUpload {
    document: string;
    expiryDate: string | null;
}

export interface CaregiverDocumentTypeUploadWithDocumentTypeId {
    caregiverDocumentTypeUpload: CaregiverDocumentTypeUpload;
    caregiverDocumentTypeId: CaregiverDocumentTypeId | null;
}

export interface HRDownloadableFilePreviewResponse {
    fileUrl: string | null;
}

export interface HRNewCaregiverOfficeAndCertification {
    officeId?: OfficeId;
    certification: CaregiverCertification;
}

export interface Office {
    id: OfficeId;
    name: string;
    agencyId: AgencyId;
    active: boolean;
    address: string;
    addressComponents: AddressComponents | null;
    timezone: string;
    hhaExternalIds?: string | null;
    phoneNumber: string | null;
    tax_id: string | null;
    npi: string | null;
    provider_id: string | null;
}



// WEBPACK FOOTER //
// ./src/messages/HR.ts